import { useEffect, useMemo, useState } from "react";
import type { WorkerDepartmentEnum } from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  PackageDetailSessionEnum,
  PackageRecurrenceEnum,
} from "@/__generated__/graphql";
import { useBookingRouterInterceptor } from "@/components/hooks/use-booking-route";
import { useFocusScreen } from "@/components/hooks/use-focus-screen";
import { useAuth } from "@/components/shared/auth-provider";
import { WebBackButtonHeader } from "@/components/shared/back-button";
import { ChooseWorkerModal } from "@/components/shared/booking/choose-worker-modal";
import { SelectAddressModal } from "@/components/shared/booking/select-address-modal";
import { CaregiverPortfolio } from "@/components/shared/caregiver-portfolio";
import { Divider } from "@/components/shared/divider";
import { IfElse } from "@/components/shared/if-else";
import { AddAddressModal } from "@/components/shared/login-register/add-address-modal";
import { WorkerPortfolio } from "@/components/shared/worker-portfolio";
import { View } from "@/components/ui";
import { Column, Container } from "@/components/ui/layout";
import {
  getBookingPackages,
  getDateRangeFromFrequency,
  getSelectedPackagesFromIds,
  getWorkerSkillIdsFromPackages,
  timeRanges,
} from "@/lib/booking-lib";
import { formatDate } from "@/lib/helpers/date";
import { sanitizeOptionalText } from "@/lib/helpers/string";
import { getWorkerLabel } from "@/lib/service/helpers";
import { getCapitalizeWord, getPlatform } from "@/lib/utils";
import { useAuthState } from "@/store/auth";
import { useAddressByPostalCodeStore } from "@/store/auth/addressByPostalCode";
import { useClientStore } from "@/store/auth/client";
import type { AddressFormData } from "@/store/auth/forms/useAddressForm";
import { usePostalCodeForm } from "@/store/auth/forms/usePostalCodeForm";
import { useClientCancelReservationStore } from "@/store/booking/clientCancelReservation";
import { useClientFindBookingScheduleQuery } from "@/gqlhooks/booking/useClientFindBookingScheduleQuery";
import { useCreateAddressStore } from "@/store/booking/createAddress";
import { useBookingState } from "@/store/booking/useBookingState";
import type { TimeSlot } from "@/types/booking";
import { PackageFrequencyGroupEnum } from "@/types/booking";
import type { ServiceName } from "@/types/service";
import type { Address } from "@/types/users";
import { addDays } from "date-fns";

import { OverdueInvoiceModal } from "./overdue-invoice-modal";
import { SelectSlotNavigation } from "./select-slot-navigation";
import { SelectSlotSection } from "./select-slot-section";
import { ServiceAddressSection } from "./service-address-section";
import { SlotExpiredModal } from "./slot-expired-modal";

export const SelectSlot = ({ name }: { name: ServiceName }) => {
  useBookingRouterInterceptor();
  const platform = getPlatform();
  const [chooseWorkerModalOpen, setChooseWorkerModalOpen] = useState(false);
  const [selectAddressModalOpen, setSelectAddressModalOpen] = useState(false);
  const [addAddressModalOpen, setAddAddressModalOpen] = useState(false);
  const [workerModalOpen, setWorkerModalOpen] = useState(false);
  const [workerSlot, setWorkerSlot] = useState<TimeSlot | null>(null);
  const {
    data: {
      department,
      selectedWorkers,
      postalCode,
      timeSlots,
      startDate,
      endDate,
      selectedSlot,
      selectedAddress,
      packageDetails,
      selectedPackageIds,
      packageDetailsFilter,
      reservations,
    },
    setBookingState,
  } = useBookingState();
  const {
    data: { userInfo },
    showModal,
    setPostalCode: setDefaultPostalCode,
  } = useAuthState();

  const { isAuthenticated } = useAuth();

  const [showSlotSection, setShowSlotSection] = useState(isAuthenticated);

  const {
    fetch: getAddressByPostalCode,
    loading: getAddressLoading,
    error: getAddressError,
  } = useAddressByPostalCodeStore();

  const {
    fetch: createAddress,
    loading: createAddressLoading,
    error: createAddressError,
  } = useCreateAddressStore();

  const { fetch: getClientInfo } = useClientStore();

  const {
    execute: getSlots,
    loading: scheduleLoading,
    transformedData,
  } = useClientFindBookingScheduleQuery();

  const { fetch: cancelReservations } = useClientCancelReservationStore();

  useFocusScreen(() => {
    if (!reservations.length) {
      refreshSlot();
    }
  });

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = usePostalCodeForm();

  const chosenAddress = useMemo(() => {
    return selectedAddress || userInfo.addresses.find((addr) => addr.primary);
  }, [selectedAddress, userInfo.addresses]);

  const workerLabel = useMemo(
    () => getCapitalizeWord(getWorkerLabel(department)),
    [],
  );

  const submitAddress = handleSubmit(
    ({ postalCode }: { postalCode: string }) => {
      setBookingState({ postalCode });
    },
  );

  const refreshSlot = async () => {
    setBookingState({ selectedSlot: null });
    if (isAuthenticated && !userInfo?.user?.phoneNumberVerifiedAt)
      await fetchClientInfo();
    if (selectedAddress?.postalCode || postalCode) {
      const selectedPackageDetails = getSelectedPackagesFromIds(
        selectedPackageIds,
        packageDetails,
      );

      getSlots({
        variables: {
          clientId: userInfo.id || "",
          department: department as unknown as WorkerDepartmentEnum,
          startDate: formatDate(startDate),
          endDate:
            packageDetailsFilter.frontEndFrequency ===
            PackageFrequencyGroupEnum.NEXT_DAY_AD_HOC
              ? formatDate(addDays(startDate, 1))
              : formatDate(endDate),
          lineItemIds: selectedPackageIds,
          postalCode: isAuthenticated
            ? (selectedAddress?.postalCode as string)
            : postalCode,
          sessionDuration: 0,
          timeRanges,
          workerSkillIds: getWorkerSkillIdsFromPackages(selectedPackageDetails),
          workersIds: selectedWorkers.length
            ? selectedWorkers.map((worker) => worker.id)
            : null,
          hasPortfolios:
            department === PackageDepartmentEnum.HomeBeauty ||
            department === PackageDepartmentEnum.ElderCare,
        },
      });
    }
  };

  const fetchClientInfo = () => {
    return getClientInfo({
      requestPayload: {
        id: userInfo.id,
      },
    });
  };

  const onSearchPostalCode = () => {
    if (!errors.postalCode?.message) {
      setShowSlotSection(true);
      submitAddress();
    }
  };

  const getAddress = handleSubmit(
    async ({ postalCode }: { postalCode: string }) => {
      const validAddress = await getAddressByPostalCode(
        {
          requestPayload: {
            postalCode,
          },
        },
        { selfHandleError: true },
      );
      if (validAddress.data?.address) {
        setBookingState({
          selectedAddress: {
            postalCode,
            fullAddress: validAddress.data.address,
            primary: true,
            active: true,
          },
        });
      }
    },
  );

  useEffect(() => {
    setBookingState({ timeSlots: null });
    if (reservations.length) {
      cancelReservations(
        {
          requestPayload: {
            input: {
              reservationIds: reservations.map((res) => res.id),
            },
          },
        },
        {
          // cancel reservation might fail, no need to notify user cause it's not important for them.
          selfHandleError: true,
        },
      ).finally(() => {
        setBookingState({ reservations: [], reservationTimeStamp: 0 });
      });
    }
  }, []);

  useEffect(() => {
    if (getAddressError)
      setError("postalCode", { message: "Invalid PostalCode" });
  }, [getAddressError]);

  useEffect(() => {
    if (isAuthenticated) {
      setBookingState({
        selectedAddress: userInfo.addresses.find(
          (addr) => addr.primary,
        ) as Address,
      });
    }
  }, [userInfo.addresses]);

  useEffect(() => {
    if (name === "home-cleaning") {
      const packages = getBookingPackages(
        department,
        packageDetails,
        selectedSlot,
      );

      // package price is depend on which slot user choose.
      const slotMappedPackageDetails = packageDetails.map((packageDetail) => {
        const pkgSession =
          selectedSlot?.partOfDay === "Evening"
            ? PackageDetailSessionEnum.Evening
            : PackageDetailSessionEnum.Day;
        if (pkgSession === packageDetail.session) {
          return {
            ...packageDetail,
            serviceBillingValue:
              (selectedSlot?.rateValue || 0) * packageDetail.units,
            unitValue: selectedSlot?.rateValue || 0,
          };
        }
        return packageDetail;
      });

      setBookingState({
        selectedPackageIds: packages.map((pkg) => pkg.id),
        packageDetails: slotMappedPackageDetails,
      });
    }
  }, [selectedSlot]);

  useEffect(() => {
    return () => {
      setBookingState({
        postalCode: "",
        startDate: initialStartDate,
        endDate: addDays(initialStartDate, 6),
      });
      setDefaultPostalCode("");
    };
  }, []);

  useEffect(() => {
    refreshSlot();
  }, [startDate, endDate, selectedAddress?.id, postalCode, selectedWorkers]);

  useEffect(() => {
    if (transformedData) {
      setBookingState({ timeSlots: transformedData });
      setDefaultPostalCode(postalCode);
    }
  }, [transformedData]);

  const initialStartDate = useMemo(() => {
    if (packageDetailsFilter.frontEndFrequency) {
      return getDateRangeFromFrequency(
        packageDetailsFilter.frontEndFrequency,
      )[0];
    }
    if (
      packageDetailsFilter?.repeatEvery?.[0] === PackageRecurrenceEnum.AdHoc
    ) {
      return new Date();
    }
    return addDays(new Date(), 1);
  }, [
    packageDetailsFilter.frontEndFrequency,
    packageDetailsFilter.repeatEvery?.[0],
  ]);

  const onSubmitAddressForm = ({
    label,
    postalCode,
    fullAddress,
    unitFloor,
    apartmentNumber,
    primary,
  }: AddressFormData) => {
    createAddress({
      requestPayload: {
        input: {
          clientId: userInfo.id,
          label: sanitizeOptionalText(label),
          postalCode: postalCode,
          fullAddress: fullAddress,
          unitNumber: `#${unitFloor || 0}-${apartmentNumber || 0}`,
          primary: !!primary,
        },
      },
    })
      .then(() => {
        setAddAddressModalOpen(false);
        showModal();
        return fetchClientInfo();
      })
      .then((clientInfo) => {
        const addr = clientInfo.data?.userInfo.addresses?.find(
          (ad) =>
            ad.postalCode === postalCode && ad.fullAddress === fullAddress,
        );
        setBookingState({
          selectedAddress: addr,
        });
      });
  };

  const onClickWorkerPortfolio = (slot: TimeSlot) => {
    setWorkerModalOpen(true);
    setWorkerSlot(slot);
  };

  return (
    <View className="bg-white">
      <Container>
        <Column desktop="8">
          <WebBackButtonHeader title="Select Service Address and Slot" />
          <View className="flex flex-col gap-3 pt-4 md:pt-0">
            {workerSlot &&
              (department === PackageDepartmentEnum.ElderCare ||
              department === PackageDepartmentEnum.Babysitter ? (
                <CaregiverPortfolio
                  openModal={workerModalOpen}
                  onClose={() => setWorkerModalOpen(false)}
                  cleanerProfilePhoto={workerSlot?.avatarUrl || ""}
                  workerName={workerSlot.workerName ?? ""}
                  description={
                    workerSlot.workerPortfolios.length
                      ? workerSlot.workerPortfolios[0].description
                      : "-"
                  }
                />
              ) : (
                <WorkerPortfolio
                  serviceTypes={packageDetailsFilter?.serviceTypes ?? []}
                  openModal={workerModalOpen}
                  onClose={() => setWorkerModalOpen(false)}
                  cleanerProfilePhoto={workerSlot?.avatarUrl || ""}
                  serviceCleaner={workerSlot?.workerName || ""}
                  serviceRating={workerSlot.workerRating || 0}
                  reviewNumber={workerSlot.rateValue || 0}
                  slotDay={workerSlot.date}
                  slotStartTime={workerSlot.startTime}
                  slotEndTime={workerSlot.endTime}
                  images={workerSlot.portfolios?.map((p) => p.imgUrl) || []}
                />
              ))}

            <ServiceAddressSection
              getAddressByPostalCode={getAddress}
              loading={getAddressLoading}
              control={control}
              isLoggedIn={!!isAuthenticated}
              chosenAddress={chosenAddress as Address}
              onSearchPostalCode={onSearchPostalCode}
              onChangeAddress={() => {
                setSelectAddressModalOpen(true);
              }}
            />

            <IfElse if={showSlotSection}>
              <Divider className="border md:mx-4" />

              <SelectSlotSection
                name={name}
                initialStartDate={initialStartDate}
                department={department}
                showTimeRange={Boolean(
                  isAuthenticated || (!isAuthenticated && postalCode),
                )}
                startDate={startDate}
                endDate={endDate}
                setStartDate={(startDate) => setBookingState({ startDate })}
                setEndDate={(endDate) => setBookingState({ endDate })}
                setSelectedSlot={(selectedSlot) =>
                  setBookingState({ selectedSlot })
                }
                loading={scheduleLoading}
                workerIds={selectedWorkers?.map((worker) => worker.id) || []}
                workerLabel={workerLabel}
                timeSlots={timeSlots}
                selectedSlot={selectedSlot}
                onChooseWorkers={() => setChooseWorkerModalOpen(true)}
                enableChooseWorker={
                  (!!isAuthenticated &&
                    department === PackageDepartmentEnum.HomeCleaning) ||
                  department === PackageDepartmentEnum.HomeBeauty
                }
                onClickWorkerPortfolio={onClickWorkerPortfolio}
              />
            </IfElse>
          </View>
        </Column>
        {platform === "web" && (
          <Column desktop="4">
            <View className="pt-6 md:sticky md:top-14">
              <SelectSlotNavigation name={name} />
            </View>
          </Column>
        )}

        <ChooseWorkerModal
          open={chooseWorkerModalOpen}
          department={department}
          workerLabel={workerLabel}
          setOpen={setChooseWorkerModalOpen}
          selectedWorkers={selectedWorkers}
          handleApply={(workers) =>
            setBookingState({ selectedWorkers: workers })
          }
        />

        <SelectAddressModal
          open={selectAddressModalOpen}
          selectedAddress={selectedAddress}
          setOpen={setSelectAddressModalOpen}
          setSelectedAddress={(selectedAddress) =>
            setBookingState({ selectedAddress })
          }
          addresses={userInfo.addresses}
          onAddAddress={() => {
            setSelectAddressModalOpen(false);
            setAddAddressModalOpen(true);
          }}
        />

        <AddAddressModal
          onClose={() => setAddAddressModalOpen(false)}
          open={addAddressModalOpen}
          onSubmitForm={onSubmitAddressForm}
          loading={createAddressLoading}
          error={createAddressError || ""}
        />

        <SlotExpiredModal />
        <OverdueInvoiceModal />
      </Container>
    </View>
  );
};
